/**
 * A MOVIMENTAÇÃO — transferência e repicagem, a mesma ficha.
 *
 * Não sobra diferença estrutural entre as duas: represa de origem, um ou mais
 * destinos, viagens do trator, e no fim a origem esvazia ou não. O que muda é
 * o vocabulário — uma move o lote inteiro do alevinário, a outra distribui um
 * resto que sobrou de despesca.
 *
 * ── CADA VIAGEM SOBE SOZINHA ──────────────────────────────────────────────
 *
 * Redesenhada em 23/09/2026, e a cena que o gestor descreveu é a especificação
 * inteira:
 *
 *   «o operador bloqueia a tela do telefone, guarda o telefone no bolso, volta
 *    ao alevinário para buscar mais… no meio do caminho trator pode furar
 *    pneu, tratador tem que ir tratar etc, ficamos expostos durante tempos
 *    longos»
 *
 * A ficha antiga ficava aberta do começo ao fim de uma operação que dura
 * horas, com tudo guardado só no aparelho até o último toque. Agora **cada
 * viagem é um registro fechado**, e a operação vira uma sequência de fatos
 * pequenos e seguros.
 *
 * ── O DESTINO SE PLANEJA UMA VEZ ──────────────────────────────────────────
 *
 * Palavras dele: «o bloco de destino já foi planejado momentos antes, planejar
 * ele novamente seria bobagem… uma vez selecionado o bloco de destino e
 * informado o peso, aquilo está salvo».
 *
 * Então o plano — represa, fase, alvo de peixes — é escolhido uma vez e fica.
 * As viagens entram dentro dele, uma a uma, e a barra diz quando parar. Quem
 * segura isso é o `plano_id`: o ciclo que nasce na represa de destino deriva
 * dele, e é por isso que a segunda viagem acha o ciclo que a primeira abriu
 * mesmo as duas tendo saído do celular sem sinal (regra 13).
 *
 * ── O QUILO QUE SAÍA NÃO SAI MAIS ─────────────────────────────────────────
 *
 * A ficha pedia «quantos quilos a balança marcou na entrada do transporte». O
 * gestor mandou tirar: «para encher a carreta o processo precisa ser rápido
 * para não faltar oxigênio», e «a interpretação não está clara de sua função».
 *
 * Some com ele a divergência entre duas réguas que nunca batiam. Fica UMA
 * medição — os peixes contados na saída do cesto, que são os mesmos que saíram
 * e os mesmos que entraram. A biometria dá o peso médio, e o quilo vira conta.
 *
 * ── E O TRATOR SEGUE COM PEIXE EM CIMA ────────────────────────────────────
 *
 * Encerrada a operação numa represa, ela fecha e some do caminho. Abre-se um
 * plano novo para o próximo endereço, e o procedimento se repete — com o que
 * já foi entregue salvo e fora de risco.
 */

import { useEffect, useMemo, useState } from 'react'
import {
  anularRemessa, encerrarCiclo, listarRonda, listarTanques, parametrosDoCalculo,
  registrarRemessa,
  type Fase, type PontoDaRonda, type Tanque,
} from '../acesso/api'
import { descartar, novoId, pendentes } from '../acesso/fila'
import { apurar, blocoVazio, BlocosDePesagem, type Bloco } from './BlocosDePesagem'
import { modular, porQueNaoModula, type SemLotacao } from '../dominio/modulacao'
import {
  hojeDoServidor, linhasDaGuia, marcarLegado, montarGuia, oQueFicouNoBanco,
  podeDescartarSozinho, podeDesfazerRemessa, PRAZO_DESFAZER_REMESSA_DIAS,
  type Guia, type MarcaDeBanco,
} from '../dominio/movimentacao'
import { baixarCsv, nomeDeArquivo } from '../dominio/planilha'
import { GuiaPapel } from './GuiaPapel'
import { SaidasDePapel } from './Papel'
import {
  diaCurto, inteiro, m2, numero, numeroNaFaixa, soNumero,
} from '../dominio/unidades'
import { hojeISO, SeletorDeDia } from './SeletorDeDia'

const RASCUNHO = 'cunhatai.movimentacao'

const FASES: readonly { valor: Fase; rotulo: string }[] = [
  { valor: 'engorda', rotulo: 'engorda' },
  { valor: 'juvenil', rotulo: 'juvenil' },
  { valor: 'alevinagem', rotulo: 'alevinagem' },
]

/** Uma viagem do trator, já gravada. */
interface Remessa {
  /** O identificador que foi para a fila — serve para desfazer. */
  id: string
  quantidade: number
  hora: string
}

/** Um destino planejado, com as viagens que já desceram nele. */
interface Plano {
  planoId: string
  tanqueId: string
  fase: Fase
  remessas: Remessa[]
  encerrado: boolean
  /**
   * Alguma viagem já foi salva aqui, mesmo que todas tenham sido desfeitas.
   *
   * A primeira viagem ABRE UM CICLO na represa de destino, e desfazê-la não
   * fecha esse ciclo. Destrancar o seletor depois disso deixaria o operador
   * apontar o plano para outra represa e abandonar um ciclo vazio na primeira
   * — que passaria a aparecer na Ronda e no Trato pedindo leitura e ração de
   * um tanque que ninguém encheu.
   */
  tocado: boolean
  /**
   * Alguma viagem deste destino chegou ao banco e depois foi desfeita.
   *
   * Quem abre o ciclo no destino é a primeira viagem que CHEGA ao banco, não a
   * última a ser desfeita. Olhando só a viagem da vez, o aviso sumia conforme a
   * ordem: sobe a 1, a 2 fica na fila, desfaz-se a 1 (sobra uma, sem aviso),
   * desfaz-se a 2 (está na fila, sem aviso) — e o lote vazio ficava aberto sem
   * ninguém saber. Achado da revisão de 23/09/2026.
   *
   * «talvez» é o rascunho de antes da v61, que não tinha esta marca — ver
   * `marcarLegado`.
   */
  algumaSubiu?: MarcaDeBanco
}

interface Rascunho {
  operacaoId: string
  /**
   * O dia em que este aparelho MEXEU no rascunho pela última vez — não o `dia`
   * lançado, e não o dia em que a tela abriu.
   *
   * Não o `dia`: quem abre hoje uma movimentação com a data de ontem e volta
   * cinco minutos depois perderia a operação. E não a abertura: a tela aberta
   * desde a véspera, com a amostra pesada às 6h, carimbaria a véspera — e a
   * amostra de hoje sumiria no primeiro recarregar. Carimbando a cada gravação,
   * o que se mexeu hoje é de hoje.
   */
  mexidaEm?: string
  origemId: string
  dia: string
  blocos: Bloco[]
  pesoAlvo: string
  planos: Plano[]
  observacao: string
}

function planoVazio(): Plano {
  return {
    planoId: novoId(), tanqueId: '', fase: 'engorda', remessas: [],
    encerrado: false, tocado: false,
  }
}

/**
 * O que a tela acha no aparelho ao abrir: o rascunho a retomar, ou a data da
 * operação que foi descartada — para a tela DIZER que descartou.
 */
interface Lido {
  rascunho: Rascunho | null
  descartado: string | null
}

function lerRascunho(): Lido {
  const nada: Lido = { rascunho: null, descartado: null }
  try {
    const bruto = localStorage.getItem(RASCUNHO)
    const r = bruto ? (JSON.parse(bruto) as Rascunho) : null
    // A ficha antiga tinha `destinos`, não `planos`. Sem esta conferência, um
    // rascunho velho no aparelho derrubaria a tela nova com `undefined.map`.
    if (!r || !Array.isArray(r.planos) || !Array.isArray(r.blocos)) return nada

    // OPERAÇÃO SEM VIAGEM DE PÉ, PARADA DESDE OUTRO DIA, NÃO VOLTA. Foi o que o
    // gestor achou em 23/09/2026: testou com a data de ontem, desfez tudo, e a
    // tela reabriu no dia seguinte com a data velha e dois destinos «encerrada
    // · 0 peixes». Sem viagem salva não há fato nenhum a perder — o que ele
    // digitou é mesa de trabalho, e mesa de ontem não se serve hoje.
    //
    // COM viagem salva, volta sempre: aí há peixe no caminho, e quem decide
    // fechar é a pessoa, pelo botão.
    //
    // E o descarte é DITO: a tela abre com uma linha contando o que saiu.
    // Descartar calado é a defesa que age sem ninguém ver.
    //
    // MAS NÃO SE ALGUMA VIAGEM CHEGOU (OU PODE TER CHEGADO) AO BANCO. Aí esta
    // mesa é o último lugar que sabe que ficou coisa no destino — o ciclo
    // aberto, a fase «misto». Ela volta, e quem descarta é a pessoa, lendo o
    // aviso da confirmação. A regra mora em `dominio/movimentacao`, com teste.
    const n = marcarLegado(r)
    if (podeDescartarSozinho(n, hojeISO())) return { rascunho: null, descartado: n.dia }
    return { rascunho: n, descartado: null }
  } catch {
    return nada
  }
}

function guardarRascunho(r: Rascunho | null): void {
  try {
    if (r) localStorage.setItem(RASCUNHO, JSON.stringify(r))
    else localStorage.removeItem(RASCUNHO)
  } catch {
    /* armazenamento bloqueado: a ficha vale só enquanto a tela estiver aberta */
  }
}

/** «14:28» — a hora de uma viagem, que é o que a torna reconhecível depois. */
function agora(): string {
  return new Date().toTimeString().slice(0, 5)
}

export function Movimentacao({ aoGravar }: { aoGravar: () => void }) {
  const lido = useMemo(lerRascunho, [])
  const salvo = lido.rascunho

  const [pontos, setPontos] = useState<PontoDaRonda[]>([])
  const [tanques, setTanques] = useState<Tanque[]>([])
  const [operacaoId] = useState(salvo?.operacaoId ?? novoId())
  const [origemId, setOrigemId] = useState(salvo?.origemId ?? '')
  const [dia, setDia] = useState(salvo?.dia ?? hojeISO())
  const [blocos, setBlocos] = useState<Bloco[]>(salvo?.blocos ?? [blocoVazio()])
  const [pesoAlvo, setPesoAlvo] = useState(salvo?.pesoAlvo ?? '')
  const [planos, setPlanos] = useState<Plano[]>(salvo?.planos ?? [])
  const [observacao, setObservacao] = useState(salvo?.observacao ?? '')
  const [retomando] = useState(salvo !== null)
  /** Desde quando a operação retomada estava parada — só para dizer na tela. */
  const [paradaDesde] = useState<string | null>(salvo?.mexidaEm ?? null)
  const [erro, setErro] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(true)

  useEffect(() => {
    Promise.all([listarRonda(dia), listarTanques(), parametrosDoCalculo()])
      .then(([r, t, p]) => {
        setPontos(r)
        setTanques(t.filter((x) => x.ativo))
        setOrigemId((atual) => (r.some((x) => x.ciclo_id === atual) ? atual : r[0]?.ciclo_id ?? ''))
        setPesoAlvo((atual) => {
          if (atual !== '') return atual
          const achado = p.parametros.find((x) => x.chave === 'movimentacao.peso_final_alvo_g')
          return achado?.valor ?? ''
        })
        setErro(null)
      })
      .catch((falha: unknown) => {
        setErro(falha instanceof Error ? falha.message : 'não deu para carregar')
      })
      .finally(() => setCarregando(false))
  }, [dia])

  /** Há alguma coisa na mesa — um destino planejado, ou uma amostra digitada. */
  const temAlgo =
    planos.length > 0 || blocos.some((b) => b.bruto !== '' || b.quantidade !== '')

  // ABRIR NÃO É MEXER. A tela grava o rascunho ao montar, com o que acabou de
  // ler; carimbar hoje nessa hora fazia uma operação largada há três dias
  // parecer mexida hoje depois da primeira olhada — e o «parada desde» sumia.
  // O carimbo só muda quando o conteúdo muda.
  const assinatura = JSON.stringify({
    operacaoId, origemId, dia, blocos, pesoAlvo, planos, observacao,
  })
  const [assinaturaLida] = useState(() =>
    salvo === null
      ? null
      : JSON.stringify({
          operacaoId: salvo.operacaoId, origemId: salvo.origemId, dia: salvo.dia,
          blocos: salvo.blocos, pesoAlvo: salvo.pesoAlvo, planos: salvo.planos,
          observacao: salvo.observacao,
        }),
  )

  useEffect(() => {
    // O RASCUNHO GUARDA A OPERAÇÃO, NÃO OS DADOS POR GRAVAR. Cada viagem já
    // subiu para a fila quando foi salva; isto aqui é a memória de onde o
    // operador estava — qual plano, quanto já desceu, o que falta. É o que faz
    // ele poder trancar a tela e voltar meia hora depois.
    guardarRascunho(
      temAlgo
        ? {
            operacaoId,
            // Rascunho antigo, não mexido, continua SEM a data: é a ausência
            // dela que `marcarLegado` lê como «de antes da v61».
            ...(assinatura === assinaturaLida
              ? (salvo?.mexidaEm === undefined ? {} : { mexidaEm: salvo.mexidaEm })
              : { mexidaEm: hojeISO() }),
            origemId, dia, blocos, pesoAlvo, planos, observacao,
          }
        : null,
    )
  }, [
    temAlgo, assinatura, assinaturaLida, salvo, operacaoId, origemId, dia, blocos, pesoAlvo,
    planos, observacao,
  ])

  const origem = pontos.find((p) => p.ciclo_id === origemId) ?? null
  const amostra = apurar(blocos, true)
  const nAlvo = numeroNaFaixa(pesoAlvo, 1, 20_000)

  /** A média vem da amostra pesada agora; sem amostra, da última balança. */
  const mediaG = amostra?.mediaG ?? origem?.peso_medido_g ?? null

  /** Qual ciclo aberto ocupa cada tanque — nulo quer dizer represa vazia. */
  const cicloDoTanque = useMemo(() => {
    const m = new Map<string, string>()
    for (const p of pontos) m.set(p.tanque_id, p.ciclo_id)
    return m
  }, [pontos])

  const entreguesTotal = planos.reduce(
    (t, p) => t + p.remessas.reduce((x, r) => x + r.quantidade, 0),
    0,
  )

  // AS REPRESAS QUE JÁ RECEBERAM NESTA OPERAÇÃO. A lista de tanques veio do
  // servidor quando a tela abriu, e as viagens de hoje ainda estão na fila —
  // então uma represa que acabou de receber 250 peixes continuaria escrita
  // «vazia» no seletor do próximo destino. Dizer «vazia» sobre uma represa que
  // a pessoa acabou de encher é o tipo de coisa que faz alguém duvidar da tela
  // inteira.
  const recebidosNestaOperacao = useMemo(() => {
    const m = new Map<string, number>()
    for (const p of planos) {
      const soma = p.remessas.reduce((t, r) => t + r.quantidade, 0)
      if (p.tanqueId !== '' && soma > 0) m.set(p.tanqueId, (m.get(p.tanqueId) ?? 0) + soma)
    }
    return m
  }, [planos])

  if (carregando) return <p className="aviso">Carregando…</p>
  if (pontos.length === 0) {
    return erro !== null ? (
      <p className="aviso-erro">Não deu para carregar os ciclos: {erro}</p>
    ) : (
      <p className="aviso">Nenhum ciclo aberto. Não há peixe para movimentar.</p>
    )
  }

  function mudarPlano(planoId: string, mudanca: Partial<Plano>) {
    setPlanos((atual) => atual.map((p) => (p.planoId === planoId ? { ...p, ...mudanca } : p)))
  }

  /**
   * LIMPA A MESA — a operação acabou, ou foi descartada.
   *
   * Não toca na fila: as viagens salvas já estão lá, e as anulações também.
   * `aoGravar` remonta a tela, e aqui isso é o que se quer — ela volta sem
   * rascunho, com operação nova e a data de hoje.
   */
  function limparOperacao() {
    guardarRascunho(null)
    setPlanos([])
    setBlocos([blocoVazio()])
    setObservacao('')
    aoGravar()
  }

  /** Grava uma viagem e a guarda na lista do plano. */
  function salvarRemessa(plano: Plano, quantidade: number) {
    if (origem === null || mediaG === null) return
    setErro(null)
    try {
      const pendente = registrarRemessa(
        {
          operacao_id: operacaoId,
          plano_id: plano.planoId,
          origem_ciclo_id: origem.ciclo_id,
          destino_tanque_id: plano.tanqueId,
          destino_fase: plano.fase,
          data: dia,
          quantidade,
          peso_medio_g: mediaG,
          observacao: observacao.trim() === '' ? null : observacao.trim(),
        },
        { rotulo: tanques.find((t) => t.id === plano.tanqueId)?.codigo ?? null },
      )
      mudarPlano(plano.planoId, {
        remessas: [...plano.remessas, { id: pendente.id, quantidade, hora: agora() }],
        tocado: true,
      })
      // NÃO SE CHAMA `aoGravar` AQUI, e isto custou uma depuração.
      //
      // `aoGravar` sobe a chave que REMONTA a tela, para ela reler do servidor
      // depois de sincronizar. Numa ficha que fecha ao gravar isso é certo. Aqui
      // não: a operação continua aberta, e remontar no meio apagava o plano, as
      // viagens e o contador — a remessa subia para a fila e a tela voltava do
      // zero, como se nada tivesse acontecido.
      //
      // O contador de pendentes não precisa disto: `BarraDeSincronia` assina a
      // própria fila e se atualiza sozinha.
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'não deu para gravar')
    }
  }

  // A GUIA — o documento da operação, montado do que a tela tem. Só existe com
  // viagem salva. O ciclo de destino só é conhecido quando a represa já estava
  // ocupada: o novo nasce no servidor, e a guia escreve «novo ciclo».
  const guia: Guia | null =
    origem !== null && mediaG !== null && entreguesTotal > 0
      ? montarGuia({
          origem: { tanque: origem.tanque, ciclo: origem.ciclo, peixesHoje: origem.peixes_hoje },
          dia,
          mediaG,
          destinos: planos.map((p) => ({
            tanque: tanques.find((t) => t.id === p.tanqueId)?.codigo ?? '—',
            ciclo: pontos.find((x) => x.tanque_id === p.tanqueId)?.ciclo ?? null,
            fase: p.fase,
            viagens: p.remessas.map((r) => ({ hora: r.hora, quantidade: r.quantidade })),
          })),
          observacao,
        })
      : null

  return (
    <section className="bloco">
      <h2>Movimentação</h2>

      {erro && <p className="aviso-erro">{erro}</p>}
      {retomando && (
        <p className="secundario">
          Retomando uma operação que ficou aberta neste aparelho
          {paradaDesde !== null && paradaDesde !== hojeISO() && (
            <>, parada desde {diaCurto(paradaDesde)}</>
          )}
          . O que já foi salvo está guardado.
        </p>
      )}
      {lido.descartado !== null && (
        <p className="secundario">
          A operação de {diaCurto(lido.descartado)} não tinha nenhuma viagem de pé e foi
          descartada ao abrir a tela.
        </p>
      )}

      <label className="campo">
        <span className="campo-rotulo">De qual represa sai?</span>
        <select
          value={origemId}
          onChange={(e) => setOrigemId(e.target.value)}
          // TROCAR A ORIGEM NO MEIO seria trocar a origem das viagens que já
          // subiram. Elas já foram gravadas com a origem de antes, e o banco
          // não tem como saber que mudou de ideia.
          disabled={entreguesTotal > 0}
        >
          {pontos.map((p) => (
            <option key={p.ciclo_id} value={p.ciclo_id}>
              {p.tanque} · {p.ciclo}
            </option>
          ))}
        </select>
        {entreguesTotal > 0 && (
          <span className="campo-dica">
            Trancada: já saiu peixe daqui nesta operação. Para mudar de origem, encerre esta
            e comece outra.
          </span>
        )}
      </label>

      {/* A DATA TRANCA COMO A ORIGEM, e pelo mesmo motivo. As viagens já
          salvas levaram esta data; trocar aqui mandaria as próximas com outra,
          a guia imprimiria uma data só para todas, e o prazo de desfazer
          passaria a ser contado contra o dia errado. */}
      <SeletorDeDia
        dia={dia}
        aoMudar={setDia}
        trancado={
          entreguesTotal > 0
            ? 'Trancado: já saiu peixe com esta data. Para lançar em outro dia, feche esta operação.'
            : undefined
        }
      />

      {/* ---- A ORIGEM: pesa-se, porque contar demora e o oxigênio cai ------ */}
      <h3>Na origem</h3>

      <BlocosDePesagem blocos={blocos} aoMudar={setBlocos} rotulo="Amostra" />

      {amostra === null && origem?.peso_medido_g ? (
        <p className="secundario">
          Sem amostra nova, vale a última pesagem: {inteiro(origem.peso_medido_g)} g por peixe.
        </p>
      ) : null}

      {mediaG === null && (
        <p className="aviso-erro">
          Sem peso médio não dá para salvar viagem nenhuma: é ele que diz quantos peixes
          cabem em cada represa, e ele acompanha cada remessa no histórico.
        </p>
      )}

      <label className="campo">
        <span className="campo-rotulo">Peso final alvo (g)</span>
        <input
          value={pesoAlvo}
          onChange={(e) => setPesoAlvo(soNumero(e.target.value))}
          inputMode="numeric"
          placeholder="1500"
        />
        <span className="campo-dica">
          O tamanho de peixe que se pretende vender. É ele que diz quantos cabem em cada
          represa.
        </span>
      </label>

      {/* ---- OS DESTINOS: um plano de cada vez, e viagens dentro dele ------ */}
      <h3>Para onde vai</h3>

      {planos.length === 0 && (
        <p className="secundario">
          Nenhum destino planejado ainda. Planeje o primeiro, e depois é só ir salvando cada
          viagem do trator.
        </p>
      )}

      {planos.map((plano) => (
        <PlanoDeDestino
          key={plano.planoId}
          plano={plano}
          tanques={tanques}
          cicloDoTanque={cicloDoTanque}
          pontos={pontos}
          origemId={origemId}
          pesoFinalAlvoG={nAlvo}
          recebidosNestaOperacao={recebidosNestaOperacao}
          operacaoId={operacaoId}
          mediaG={mediaG}
          dia={dia}
          aoMudar={(m) => mudarPlano(plano.planoId, m)}
          aoRemover={() => setPlanos((a) => a.filter((p) => p.planoId !== plano.planoId))}
          aoSalvarRemessa={(q) => salvarRemessa(plano, q)}
        />
      ))}

      {/* UM PLANO ABERTO POR VEZ. O trator está numa represa só; oferecer o
          próximo antes de encerrar este convida a descarregar no lugar errado. */}
      {planos.every((p) => p.encerrado) && (
        <button
          type="button"
          className="discreto"
          onClick={() => setPlanos((a) => [...a, planoVazio()])}
        >
          + planejar {planos.length === 0 ? 'o primeiro destino' : 'o próximo destino'}
        </button>
      )}

      <label className="campo">
        <span className="campo-rotulo">Observação da operação (opcional)</span>
        <input
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          placeholder="chuva no meio da tarde"
        />
        <span className="campo-dica">Acompanha as viagens salvas a partir de agora.</span>
      </label>

      {guia && <ExportarGuia guia={guia} />}

      {entreguesTotal > 0 && (
        <FecharOperacao
          origem={origem}
          entregues={entreguesTotal}
          dia={dia}
          aoEncerrar={limparOperacao}
        />
      )}

      {/* SEM PEIXE ENTREGUE, A SAÍDA É ESTA. `FecharOperacao` só existe com
          viagem de pé — e era a única saída, então desfazer todas prendia a
          operação no aparelho. */}
      {entreguesTotal === 0 && temAlgo && (
        <DescartarOperacao
          desfeitas={planos.some((p) => p.tocado)}
          restouNoBanco={oQueFicouNoBanco(planos)}
          aoDescartar={limparOperacao}
        />
      )}
    </section>
  )
}

/**
 * A GUIA DA OPERAÇÃO — a folha que fica para a fazenda.
 *
 * Vem ANTES da pergunta de fechar, de propósito: fechar limpa a tela, e o que
 * a guia mostra mora só nela — as viagens já estão na fila, mas a fila não
 * sabe montar um documento. Imprimir depois de fechar não existe, e a tela
 * diz isso onde a pessoa está olhando.
 */
function ExportarGuia({ guia }: { guia: Guia }) {
  return (
    <div className="guia-saidas">
      <h3>A guia da operação</h3>
      <p className="secundario">
        {inteiro(guia.peixes)} peixes em {inteiro(guia.viagens)}{' '}
        {guia.viagens === 1 ? 'viagem' : 'viagens'}, para{' '}
        {guia.destinos.map((d) => d.tanque).join(', ')}. Imprima ou baixe antes de fechar —
        depois a tela esvazia.
      </p>
      <SaidasDePapel
        aoPlanilha={() =>
          baixarCsv(nomeDeArquivo('guia', guia.origem.ciclo, guia.dia), linhasDaGuia(guia))
        }
      />
      {/* O DOCUMENTO, invisível na tela e o único que vai para o papel. Fica
          montado para que «Imprimir» do próprio navegador funcione — inclusive
          o do menu, que não passa pelo botão. */}
      <GuiaPapel guia={guia} />
    </div>
  )
}

/**
 * UM DESTINO PLANEJADO, e as viagens que desceram nele.
 *
 * Enquanto está aberto, mostra o alvo, a barra e o contador. Encerrado, encolhe
 * para uma linha — o trator já saiu daqui, e o que interessa é o próximo.
 */
function PlanoDeDestino({
  plano, tanques, cicloDoTanque, pontos, origemId, pesoFinalAlvoG,
  recebidosNestaOperacao, mediaG, dia, operacaoId,
  aoMudar, aoRemover, aoSalvarRemessa,
}: {
  plano: Plano
  tanques: Tanque[]
  cicloDoTanque: Map<string, string>
  pontos: PontoDaRonda[]
  origemId: string
  pesoFinalAlvoG: number | null
  recebidosNestaOperacao: Map<string, number>
  operacaoId: string
  mediaG: number | null
  dia: string
  aoMudar: (m: Partial<Plano>) => void
  aoRemover: () => void
  aoSalvarRemessa: (quantidade: number) => void
}) {
  /** O contador da viagem em curso. Zera a cada remessa salva. */
  const [contando, setContando] = useState(0)
  const [desfazendo, setDesfazendo] = useState<string | null>(null)
  const [confirmandoEncerrar, setConfirmandoEncerrar] = useState(false)

  // A viagem que se vai desfazer JÁ SUBIU? Fora da fila, o banco a conhece — e
  // desfazer anula as duas linhas de peixe, mas não o que ela mudou em volta.
  const jaSubiu = desfazendo !== null && !pendentes().some((x) => x.id === desfazendo)
  // O mesmo prazo, e o mesmo «hoje», que o servidor usa — ver `hojeDoServidor`.
  const dentroDoPrazo = podeDesfazerRemessa(dia, hojeDoServidor())

  const tanque = tanques.find((t) => t.id === plano.tanqueId) ?? null
  const ocupado = plano.tanqueId === '' ? null : (cicloDoTanque.get(plano.tanqueId) ?? null)
  const jaTem = pontos.find((p) => p.ciclo_id === ocupado) ?? null
  const entregues = plano.remessas.reduce((t, r) => t + r.quantidade, 0)
  const alvo = tanque
    ? modular(m2(tanque.area_m2), tanque.carga_final_alvo_t_ha, pesoFinalAlvoG, entregues, mediaG)
    : null

  const podeSalvar =
    !plano.encerrado && plano.tanqueId !== '' && contando > 0 && mediaG !== null &&
    cicloDoTanque.get(plano.tanqueId) !== origemId

  // ---- ENCERRADO: uma linha, e sai do caminho ----------------------------
  if (plano.encerrado) {
    return (
      <div className="plano encerrado">
        <span>
          <strong>{tanque?.codigo ?? '—'}</strong>
          <span className="secundario">
            {' · '}{inteiro(entregues)} peixes em {plano.remessas.length}{' '}
            {plano.remessas.length === 1 ? 'viagem' : 'viagens'}
          </span>
        </span>
        <span className="etiqueta etiqueta-fria">encerrada</span>
      </div>
    )
  }

  return (
    <div className="plano">
      <div className="pesagem-topo">
        <span className="campo-rotulo">Destino</span>
        {/* Só some quem nunca recebeu peixe. Remover um plano que já gravou
            apagaria da tela um fato que está no banco — inclusive o ciclo que
            a primeira viagem abriu no destino. */}
        {!plano.tocado && (
          <button type="button" className="discreto" onClick={aoRemover}>
            remover
          </button>
        )}
      </div>

      <label className="campo">
        <span className="campo-rotulo">Represa</span>
        <select
          value={plano.tanqueId}
          onChange={(e) => aoMudar({ tanqueId: e.target.value })}
          // O DESTINO SE PLANEJA UMA VEZ. Depois da primeira viagem ele está
          // salvo — trocar aqui mandaria as próximas para outra represa e
          // deixaria as anteriores onde estavam, sem nada dizendo isso.
          //
          // `tocado` e não `remessas.length`: desfazer todas as viagens não
          // fecha o ciclo que a primeira abriu no destino.
          disabled={plano.tocado}
        >
          <option value="">escolha…</option>
          {tanques.map((t) => {
            const recebeu = recebidosNestaOperacao.get(t.id) ?? 0
            return (
              <option key={t.id} value={t.id} disabled={cicloDoTanque.get(t.id) === origemId}>
                {t.codigo}
                {recebeu > 0
                  ? ` · ${inteiro(recebeu)} desceram hoje`
                  : cicloDoTanque.has(t.id) ? ' · com peixe' : ' · vazia'}
              </option>
            )
          })}
        </select>
        {plano.tocado && (
          <span className="campo-dica">
            Trancado: já desceu peixe aqui, e a represa ganhou um ciclo. Encerre esta para
            planejar a próxima.
          </span>
        )}
      </label>

      {plano.tanqueId !== '' && ocupado === null && !plano.tocado && (
        <label className="campo">
          <span className="campo-rotulo">Fase do ciclo que abre aqui</span>
          <select value={plano.fase} onChange={(e) => aoMudar({ fase: e.target.value as Fase })}>
            {FASES.map((f) => (
              <option key={f.valor} value={f.valor}>{f.rotulo}</option>
            ))}
          </select>
        </label>
      )}

      {jaTem && (
        <p className="secundario">
          Já tem o ciclo {jaTem.ciclo} com {inteiro(jaTem.peixes_hoje)} peixes. O peixe entra
          nele, e o ciclo passa a misto.
        </p>
      )}

      {alvo && <Modulacao alvo={alvo} />}
      {!alvo && tanque && (
        <SemBarra
          motivo={porQueNaoModula(m2(tanque.area_m2), tanque.carga_final_alvo_t_ha, pesoFinalAlvoG)}
          codigo={tanque.codigo}
        />
      )}

      {/* ---- AS VIAGENS JÁ SALVAS ---------------------------------------- */}
      {plano.remessas.length > 0 && (
        <ul className="registrado">
          {plano.remessas.map((r, i) => (
            <li key={r.id} className="registrado-item">
              <span className="registrado-marca">✓</span>
              <span>
                <strong>{r.hora}</strong> · viagem {i + 1} ·{' '}
                {inteiro(r.quantidade)} peixes
              </span>
              {dentroDoPrazo && (
                <button
                  type="button"
                  className="discreto"
                  onClick={() => setDesfazendo(desfazendo === r.id ? null : r.id)}
                >
                  {desfazendo === r.id ? 'deixa' : 'desfazer'}
                </button>
              )}
            </li>
          ))}

          {!dentroDoPrazo && (
            <li className="registrado-item secundario">
              Viagens de mais de {PRAZO_DESFAZER_REMESSA_DIAS} dias atrás não se desfazem pelo app: a
              biomassa delas já entrou na conta do trato desses dias.
            </li>
          )}

          {desfazendo !== null && (
            <li className="registrado-item confirma-anular">
              <span>
                Desfazer esta viagem? A saída e a entrada caem juntas, e o peixe volta para a
                represa de origem.
                {/* O QUE AINDA NÃO VOLTA, dito na hora em que acontece. A última
                    viagem de um destino deixa para trás o que a primeira mudou
                    lá: o ciclo aberto na represa vazia, ou a fase «misto» no
                    lote ocupado — cujo valor anterior o banco nem guarda. Sem
                    esta frase, o desfazer parecia completo e não era. */}
                {(jaSubiu || plano.algumaSubiu === true) && plano.remessas.length === 1 && (
                  <>
                    {' '}Este destino já teve viagem chegando ao banco, e o app ainda não desfaz
                    tudo o que ela mudou lá: se o <strong>{tanque?.codigo}</strong> estava vazio,
                    fica aberto um lote sem peixe; se já tinha peixe, o lote continua marcado
                    como misto. Avise o gestor — hoje isso se corrige fora do app.
                  </>
                )}
              </span>
              <button
                type="button"
                className="confirma"
                onClick={() => {
                  // ENQUANTO ESTÁ NA FILA, DESFAZER É APAGAR. O registro nunca
                  // existiu para o banco, e enfileirar uma anulação para uma
                  // linha que o servidor nunca viu punha as duas na fila: a
                  // viagem subia, a anulação falhava atrás dela, e o peixe
                  // ficava lá enquanto a tela já dizia que não.
                  const naFila = pendentes().some((x) => x.id === desfazendo)
                  if (naFila) {
                    descartar(desfazendo)
                  } else {
                    anularRemessa(operacaoId, desfazendo, dia)
                  }
                  aoMudar({
                    remessas: plano.remessas.filter((x) => x.id !== desfazendo),
                    // A MARCA FICA: desfeita esta, o que ela abriu ou mudou no
                    // destino continua no banco, e o aviso da última viagem e o
                    // do descarte precisam saber disso.
                    ...(naFila ? {} : { algumaSubiu: true }),
                  })
                  setDesfazendo(null)
                }}
              >
                sim, desfazer
              </button>
            </li>
          )}
        </ul>
      )}

      {/* ---- O CONTADOR DA VIAGEM EM CURSO -------------------------------- */}
      <div className="viagem">
        <div className="viagem-numero">
          <strong>{inteiro(contando)}</strong> peixes nesta viagem
        </div>
        <div className="pesagem-contador">
          <button type="button" className="discreto" onClick={() => setContando((n) => n + 50)}>
            +50
          </button>
          <button type="button" className="discreto" onClick={() => setContando((n) => n + 10)}>
            +10
          </button>
          <button
            type="button"
            className="discreto"
            onClick={() => setContando((n) => Math.max(0, n - 50))}
          >
            −50
          </button>
          <button type="button" className="discreto" onClick={() => setContando(0)}>
            zerar
          </button>
        </div>

        <button
          type="button"
          className="confirma"
          disabled={!podeSalvar}
          onClick={() => {
            aoSalvarRemessa(contando)
            setContando(0)
          }}
        >
          ✓ salvar esta viagem
        </button>
      </div>

      {/* ---- ENCERRAR A REPRESA ------------------------------------------- */}
      {plano.tocado && (
        confirmandoEncerrar ? (
          <div className="confirma-desfazer">
            <span>
              Encerrar <strong>{tanque?.codigo}</strong> com {inteiro(entregues)} peixes? Ela
              fecha aqui, e o trator segue para o próximo destino.
            </span>
            <button
              type="button"
              className="confirma"
              onClick={() => { aoMudar({ encerrado: true }); setConfirmandoEncerrar(false) }}
            >
              sim, encerrar
            </button>
            <button
              type="button"
              className="discreto"
              onClick={() => setConfirmandoEncerrar(false)}
            >
              deixa
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="discreto"
            onClick={() => setConfirmandoEncerrar(true)}
          >
            encerrar operação nesta represa
          </button>
        )
      )}
    </div>
  )
}

/**
 * A SAÍDA QUE FALTAVA — descartar uma operação sem viagem de pé.
 *
 * O fechar de `FecharOperacao` só existe com peixe entregue. Desfeitas todas as
 * viagens, ele sumia, e a operação ficava presa no aparelho. O gestor achou em
 * 23/09/2026: «em caso de cancelamento de operação o visor deveria voltar ao
 * estado normal».
 *
 * Descartar aqui não apaga nada que o banco saiba. Viagem desfeita enquanto
 * estava na fila foi apagada da fila; viagem desfeita depois de subir deixou a
 * anulação na fila, e a fila não mora neste rascunho. O que sai é só a mesa de
 * trabalho — a amostra digitada e os destinos planejados.
 */
function DescartarOperacao({
  desfeitas, restouNoBanco, aoDescartar,
}: {
  desfeitas: boolean
  /** Alguma viagem chegou ao banco antes de ser desfeita — ou pode ter chegado. */
  restouNoBanco: 'sim' | 'talvez' | 'nao'
  aoDescartar: () => void
}) {
  const [confirmando, setConfirmando] = useState(false)

  return (
    <div className="fechar-operacao">
      {confirmando ? (
        <div className="confirma-desfazer">
          <span>
            {desfeitas
              ? 'Todas as viagens desta operação foram desfeitas.'
              : 'Nenhuma viagem desta operação foi salva.'}{' '}
            Descartar e limpar a tela? A amostra e os destinos digitados saem junto.
            {/* Depois do «sim», esta tela é o último lugar que sabe disso. */}
            {restouNoBanco === 'sim' && (
              <>
                {' '}<strong>Atenção:</strong> uma viagem desta operação chegou ao banco antes de
                ser desfeita, e o que ela abriu ou mudou na represa de destino continua lá.
                Avise o gestor.
              </>
            )}
            {restouNoBanco === 'talvez' && (
              <>
                {' '}<strong>Atenção:</strong> esta operação é de uma versão anterior do app, e
                não dá para saber se alguma viagem dela chegou ao banco antes de ser desfeita.
                Se chegou, o que ela abriu ou mudou na represa de destino continua lá. Avise o
                gestor.
              </>
            )}
          </span>
          <button type="button" className="confirma" onClick={aoDescartar}>
            sim, descartar
          </button>
          <button type="button" className="discreto" onClick={() => setConfirmando(false)}>
            deixa
          </button>
        </div>
      ) : (
        <button type="button" className="discreto" onClick={() => setConfirmando(true)}>
          descartar esta operação
        </button>
      )}
    </div>
  )
}

/** Por que a barra de lotação não aparece — dito, em vez de ela sumir. */
function SemBarra({ motivo, codigo }: { motivo: SemLotacao | null; codigo: string }) {
  if (motivo === null) return null
  return (
    <p className="secundario">
      A barra de lotação não aparece:{' '}
      {motivo === 'sem_carga' && (
        <>
          o <strong>{codigo}</strong> não tem carga final alvo cadastrada, e sem ela a tela
          não sabe quantos peixes cabem. Quem cadastra é o gestor, em Tanques.
        </>
      )}
      {motivo === 'sem_peso_alvo' && (
        <>o peso final alvo, lá em cima, está vazio ou fora da faixa de 1 a 20.000 g.</>
      )}
      {motivo === 'nao_cabe' && (
        <>com essa carga e esse peso final, a conta não comporta nenhum peixe.</>
      )}
      {motivo === 'sem_area' && (
        <>
          o <strong>{codigo}</strong> está sem área cadastrada.
        </>
      )}
    </p>
  )
}

/**
 * O FIM DA OPERAÇÃO — e a pergunta que só quem olhou a represa responde.
 *
 * Encerrar o ciclo de origem é ato à parte, e não consequência do último
 * lançamento: o alevinário fica vazio ao fim de uma transferência de horas, e
 * quem declara isso é a pessoa, não a aritmética.
 *
 * O saldo que a tela mostra vai junto com o encerramento — é o que permite,
 * um ano adiante, saber se ela encerrou vendo um número que hoje seria outro.
 */
function FecharOperacao({
  origem, entregues, dia, aoEncerrar,
}: {
  origem: PontoDaRonda | null
  entregues: number
  dia: string
  aoEncerrar: () => void
}) {
  const [encerrando, setEncerrando] = useState(false)
  const [limpando, setLimpando] = useState(false)
  const [remanescente, setRemanescente] = useState('')
  const nRemanescente = numeroNaFaixa(remanescente, 0, 1_000_000)
  const sobra = origem === null ? null : origem.peixes_hoje - entregues

  return (
    <div className="fechar-operacao">
      <h3>A origem esvaziou?</h3>
      <p className="secundario">
        {inteiro(entregues)} peixes saíram de {origem?.tanque ?? '—'} nesta operação.
        {sobra !== null && sobra > 0 && (
          <> A conta diz que ainda restam cerca de {inteiro(sobra)}.</>
        )}
      </p>

      {encerrando ? (
        <>
          <label className="campo">
            <span className="campo-rotulo">Quantos ficaram, se você contou (opcional)</span>
            <input
              value={remanescente}
              onChange={(e) => setRemanescente(soNumero(e.target.value))}
              inputMode="numeric"
              placeholder={sobra !== null && sobra > 0 ? String(sobra) : '0'}
            />
            <span className="campo-dica">
              Em branco, fica registrado o que a tela mostrava. A diferença é diagnóstico, não
              acusação — ela vira o saldo residual do ciclo.
            </span>
          </label>

          <div className="confirma-desfazer">
            <span>
              Encerrar o ciclo de <strong>{origem?.tanque}</strong>? A represa zera e para de
              mostrar saldo. Isso não se desfaz pelo app.
            </span>
            <button
              type="button"
              className="confirma"
              onClick={() => {
                if (origem === null) return
                encerrarCiclo(
                  {
                    ciclo_id: origem.ciclo_id,
                    data: dia,
                    saldo_residual_mostrado: nRemanescente ?? sobra,
                    observacao: 'encerrado ao fim de uma transferência',
                  },
                  { rotulo: origem.tanque },
                )
                setEncerrando(false)
                aoEncerrar()
              }}
            >
              sim, encerrar o ciclo
            </button>
            <button type="button" className="discreto" onClick={() => setEncerrando(false)}>
              deixa
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="apuracao">
            <button type="button" className="unidade-botao" onClick={() => setEncerrando(true)}>
              sim, encerra o ciclo
            </button>
            <button
              type="button"
              className="unidade-botao"
              onClick={() => setLimpando(true)}
            >
              não, a operação acabou aqui
            </button>
          </div>

          {/* OS DOIS BOTÕES TÊM O MESMO PESO, e um deles limpava a tela num
              toque. Nada se perde do banco — as viagens já estão na fila —,
              mas quem está olhando a tela não tem como saber disso, e não há
              como voltar. Confirmar custa um toque e devolve o caminho. */}
          {limpando && (
            <div className="confirma-desfazer">
              <span>
                Fechar esta operação na tela? Os {inteiro(entregues)} peixes já salvos
                continuam na fila e sobem na próxima sincronização — o que sai daqui é só o
                acompanhamento, e a guia: se precisar dela impressa, é antes.
              </span>
              <button type="button" className="confirma" onClick={aoEncerrar}>
                sim, fechar
              </button>
              <button type="button" className="discreto" onClick={() => setLimpando(false)}>
                deixa
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

/**
 * A BARRA — quanto cabe, quanto já entrou.
 *
 * Ela existe para quem está contando saber quando parar, sem ter de ligar para
 * o gestor com o trator carregado. Abaixo dela, o que falta em peixes E EM
 * QUILOS: quilo é a língua da carga, e quem carrega faz a divisão sozinho pelo
 * equipamento que conhece.
 *
 * Não se diz «cerca de uma viagem e meia» — isso exigiria adivinhar o
 * equipamento, e caixa de mil litros e tambor de duzentos não são a mesma
 * viagem.
 */
function Modulacao({ alvo }: { alvo: ReturnType<typeof modular> }) {
  if (!alvo) return null
  const pct = Math.min(100, Math.round(alvo.fracao * 100))
  return (
    <div className="modulacao">
      <div className="modulacao-barra">
        <div className="modulacao-cheio" style={{ width: `${pct}%` }} />
      </div>
      <div className="secundario">
        {inteiro(alvo.peixes - alvo.faltam)} de {inteiro(alvo.peixes)} peixes · {pct}%
        {alvo.faltam > 0 && (
          <>
            {' · faltam '}
            {inteiro(alvo.faltam)}
            {alvo.faltamKg !== null && <> — cerca de {numero(alvo.faltamKg, 0)} kg</>}
          </>
        )}
      </div>
    </div>
  )
}
